import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthProvider';
import { toast } from 'sonner';
import { Connection, PublicKey, LAMPORTS_PER_SOL, SystemProgram, Transaction, Keypair } from '@solana/web3.js';
import bs58 from 'bs58';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================
// SOLANA RPC CONFIGURATION - Uses Backend Proxy to bypass CORS
// ============================================================
// Backend proxy endpoint for RPC calls (bypasses browser CORS)
const RPC_PROXY_URL = `${API}/solana/rpc`;
// Solana RPC Configuration - Use PUBLIC Solana mainnet (no Helius)
const DIRECT_RPC_ENDPOINT = 'https://api.mainnet-beta.solana.com';

// $SLAP Token Mint Address (CRITICAL: Must match on-chain token)
const SLAP_TOKEN_MINT = 'A7rKF3srg28UArWp1ibVVUbQmnjUnitTtq7ZFR4tpump';

// ============================================================
// TREASURY WALLET - ALL HOUSE PROFITS GO HERE (NO BURN)
// ============================================================
// Entry fees, game losses, dev tax - ALL goes to Treasury
const TREASURY_WALLET = 'GntfPdLmhjJ2unKJ9fp7S6rDz7AA1Hkn52pKpd9TceCa';

// Connection config: 'confirmed' is faster than 'finalized' (seconds vs minutes)
const CONNECTION_COMMITMENT = 'confirmed';

// Auto-refresh interval (15 seconds for faster updates)
const BALANCE_REFRESH_INTERVAL = 15000;

// Retry config for balance fetch
const BALANCE_RETRY_COUNT = 3;
const BALANCE_RETRY_DELAY = 2000; // 2 seconds

// Connection timeout before showing error (10 seconds)
const CONNECTION_ERROR_DELAY = 10000;

const GameWalletContext = createContext(null);

export const useGameWallet = () => {
  const context = useContext(GameWalletContext);
  if (!context) {
    throw new Error('useGameWallet must be used within a GameWalletProvider');
  }
  return context;
};

export const GameWalletProvider = ({ children }) => {
  const { user, walletAddress, refreshUser, connected, solanaAddress, secretKey, localWallet } = useAuth();
  
  // Wallet state - CRITICAL: Initialize ALL balances at 0, NEVER mock values
  const [credits, setCredits] = useState(0); // In-game credits from DB
  const [slapBalance, setSlapBalance] = useState(0); // $SLAP tokens from BLOCKCHAIN (REAL)
  const [solBalance, setSolBalance] = useState(0); // SOL balance from blockchain
  const [isWalletActive, setIsWalletActive] = useState(false);
  const [isDepositing, setIsDepositing] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'disconnected', 'connecting', 'connected', 'error'
  const [rpcEndpoint, setRpcEndpoint] = useState(null); // Track which RPC we're using
  const [rpcUrl, setRpcUrl] = useState(DIRECT_RPC_ENDPOINT); // Actual RPC URL for transactions
  const [lastBalanceFetch, setLastBalanceFetch] = useState(null); // Track last fetch time
  const [balanceError, setBalanceError] = useState(null); // Track balance fetch errors
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(false);

  // Solana connection ref
  const connectionRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  // Initialize Solana connection - WITH FALLBACK
  useEffect(() => {
    let isMounted = true;
    let errorTimeoutId = null;
    
    // Helper function to make RPC calls via backend proxy
    const rpcCall = async (method, params = []) => {
      try {
        const response = await axios.post(RPC_PROXY_URL, {
          jsonrpc: "2.0",
          id: 1,
          method,
          params
        });
        return response.data;
      } catch (err) {
        console.warn(`[GameWallet] Proxy RPC call ${method} failed:`, err.message);
        throw err;
      }
    };
    
    const tryConnect = async (endpoint, name) => {
      try {
        console.log(`[GameWallet] 🔌 Trying ${name} RPC via backend proxy...`);
        
        // Use backend proxy for connection test (bypasses browser CORS)
        const result = await rpcCall('getBlockHeight');
        
        if (result && result.result) {
          console.log(`[GameWallet] ✅ ${name} RPC Connected via proxy! Block height:`, result.result);
          
          // Create connection for transaction signing (needed for sendTransaction)
          const connection = new Connection(endpoint, {
            commitment: CONNECTION_COMMITMENT,
            confirmTransactionInitialTimeout: 60000,
          });
          
          return { connection, blockHeight: result.result };
        }
        throw new Error('Invalid proxy RPC response');
      } catch (err) {
        console.warn(`[GameWallet] ⚠️ ${name} RPC failed:`, err.message);
        return null;
      }
    };
    
    const initConnection = async () => {
      if (!isMounted) return;
      
      setConnectionStatus('connecting');
      setRpcEndpoint(null);
      
      // Try via backend proxy (bypasses browser CORS issues)
      let result = await tryConnect(DIRECT_RPC_ENDPOINT, 'Backend Proxy');
      
      if (!isMounted) return;
      
      if (result) {
        connectionRef.current = result.connection;
        setRpcEndpoint('Connected');
        setRpcUrl(DIRECT_RPC_ENDPOINT);
        setConnectionStatus('connected');
        
        toast.success('🟢 Connected to Solana', {
          description: `Block: ${result.blockHeight.toLocaleString()}`,
          duration: 2000
        });
      } else {
        // Wait before showing error (give network time)
        errorTimeoutId = setTimeout(() => {
          if (isMounted) {
            setConnectionStatus('error');
            setRpcEndpoint('Error');
            toast.error('Network Unavailable', {
              description: 'Could not connect to Solana. Retrying...',
              duration: 5000
            });
            // Auto-retry after error
            setTimeout(() => {
              if (isMounted) initConnection();
            }, 5000);
          }
        }, CONNECTION_ERROR_DELAY);
      }
    };
    
    initConnection();
    
    return () => {
      isMounted = false;
      if (errorTimeoutId) clearTimeout(errorTimeoutId);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Fetch SOL balance from blockchain - WITH AUTO-RETRY
  const fetchSolBalance = useCallback(async (showToast = false, forceRefresh = false) => {
    const address = solanaAddress || localWallet?.publicKey;
    if (!address) {
      console.log('[GameWallet] No wallet address available');
      return 0;
    }

    setIsRefreshing(true);
    setLastBalanceFetch(new Date().toISOString());
    
    let lastError = null;
    let fetchedBalance = 0;
    
    // AUTO-RETRY: Try up to 3 times with 2s delay
    for (let attempt = 1; attempt <= BALANCE_RETRY_COUNT; attempt++) {
      try {
        console.log(`[GameWallet] 📊 Fetching balance via proxy (attempt ${attempt}/${BALANCE_RETRY_COUNT})...`);
        
        // Use backend proxy to bypass CORS
        const response = await axios.post(RPC_PROXY_URL, {
          jsonrpc: "2.0",
          id: 1,
          method: "getBalance",
          params: [address, { commitment: CONNECTION_COMMITMENT }]
        });
        
        if (response.data && response.data.result && typeof response.data.result.value === 'number') {
          fetchedBalance = response.data.result.value / LAMPORTS_PER_SOL;
          console.log(`[GameWallet] ✅ Balance fetched via proxy: ${fetchedBalance} SOL`);
          lastError = null;
          break;
        }
        throw new Error('Invalid balance response from proxy');
        
      } catch (error) {
        lastError = error;
        console.warn(`[GameWallet] ⚠️ Fetch attempt ${attempt} failed:`, error.message);
        
        // Wait before retry (except on last attempt)
        if (attempt < BALANCE_RETRY_COUNT) {
          await new Promise(resolve => setTimeout(resolve, BALANCE_RETRY_DELAY));
        }
      }
    }
    
    // Handle final result
    if (lastError) {
      console.error('[GameWallet] ❌ All fetch attempts failed:', lastError.message);
      if (showToast) {
        toast.error('Failed to fetch balance', {
          description: `After ${BALANCE_RETRY_COUNT} attempts: ${lastError.message}`
        });
      }
      setIsRefreshing(false);
      return solBalance; // Return cached balance
    }
    
    // Check if balance changed (deposit detection)
    if (fetchedBalance !== solBalance) {
      if (solBalance > 0 && fetchedBalance > solBalance) {
        const depositAmount = fetchedBalance - solBalance;
        toast.success('💰 Deposit Received!', {
          description: `+${depositAmount.toFixed(6)} SOL detected`,
          duration: 5000
        });
      }
      setSolBalance(fetchedBalance);
    }
    
    if (showToast) {
      toast.success('Balance Updated', {
        description: `SOL: ${fetchedBalance.toFixed(6)}`,
        icon: '🔄'
      });
    }
    
    setIsRefreshing(false);
    return fetchedBalance;
  }, [solanaAddress, localWallet, solBalance]);

  // ============================================================
  // CRITICAL: Fetch REAL $SLAP token balance from blockchain (via proxy)
  // ============================================================
  const fetchSlapTokenBalance = useCallback(async (showToast = false) => {
    const address = solanaAddress || localWallet?.publicKey;
    
    if (!address) {
      console.log('[GameWallet] ⚠️ Cannot fetch $SLAP: No wallet');
      return 0;
    }
    
    try {
      console.log('[GameWallet] 📊 Fetching REAL $SLAP token balance via proxy...');
      
      // Use backend proxy to get token accounts (bypasses CORS)
      const response = await axios.post(RPC_PROXY_URL, {
        jsonrpc: "2.0",
        id: 1,
        method: "getTokenAccountsByOwner",
        params: [
          address,
          { mint: SLAP_TOKEN_MINT },
          { encoding: "jsonParsed", commitment: CONNECTION_COMMITMENT }
        ]
      });
      
      let totalSlapBalance = 0;
      
      if (response.data && response.data.result && response.data.result.value) {
        const tokenAccounts = response.data.result.value;
        
        if (tokenAccounts.length > 0) {
          // Sum up all $SLAP token accounts (usually just 1)
          for (const account of tokenAccounts) {
            const tokenAmount = account.account?.data?.parsed?.info?.tokenAmount;
            if (tokenAmount) {
              totalSlapBalance += parseFloat(tokenAmount.uiAmount || 0);
            }
          }
          console.log(`[GameWallet] ✅ REAL $SLAP balance: ${totalSlapBalance}`);
        } else {
          console.log('[GameWallet] ℹ️ No $SLAP token account found (balance = 0)');
        }
      }
      
      // Update state with REAL balance
      setSlapBalance(totalSlapBalance);
      setCredits(totalSlapBalance); // Also update credits for game use
      setBalanceError(null);
      
      if (showToast && totalSlapBalance > 0) {
        toast.success('$SLAP Balance Updated', {
          description: `${totalSlapBalance.toLocaleString()} $SLAP (from blockchain)`,
          icon: '💰'
        });
      }
      
      return totalSlapBalance;
      
    } catch (error) {
      console.error('[GameWallet] ❌ Failed to fetch $SLAP balance:', error.message);
      setBalanceError('Failed to fetch $SLAP balance');
      // DO NOT set to fake value - show 0 or error
      return 0;
    }
  }, [solanaAddress, localWallet]);

  // Auto-refresh ALL balances (SOL + $SLAP)
  useEffect(() => {
    const address = solanaAddress || localWallet?.publicKey;
    if (!address) return;

    // Initial fetch - BOTH balances (use small delay to let proxy connect)
    setTimeout(() => {
      fetchSolBalance();
      fetchSlapTokenBalance();
    }, 1000);

    // Set up auto-refresh for BOTH
    refreshIntervalRef.current = setInterval(() => {
      fetchSolBalance();
      fetchSlapTokenBalance();
    }, BALANCE_REFRESH_INTERVAL);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [solanaAddress, localWallet, fetchSolBalance, fetchSlapTokenBalance]);

  // Sync with user data - ONLY for wallet active state, NOT balance
  // CRITICAL: Balance comes from BLOCKCHAIN, not from user DB
  useEffect(() => {
    if (user) {
      setIsWalletActive(true);
      
      // Check if first-time visitor
      const seenOnboarding = localStorage.getItem('gameWallet_onboarding_seen');
      if (!seenOnboarding && user.total_shards_earned === 0) {
        setShowOnboarding(true);
      }
      
      // DO NOT set balance from user.slap_tokens - use blockchain data only
      // The fetchSlapTokenBalance() function handles real balance
    } else {
      // Reset to 0 when no user - NEVER use fake values
      setCredits(0);
      setSlapBalance(0);
      setIsWalletActive(false);
    }
  }, [user]);

  // INSTANT WITHDRAWAL - Sign and broadcast transaction immediately
  const withdrawSol = useCallback(async (destinationAddress, amountSol) => {
    const address = solanaAddress || localWallet?.publicKey;
    const privateKey = secretKey || localWallet?.secretKey;
    
    if (!address || !privateKey) {
      toast.error('No wallet connected');
      return { success: false, error: 'No wallet' };
    }

    if (!connectionRef.current) {
      toast.error('Not connected to Solana');
      return { success: false, error: 'No connection' };
    }

    if (amountSol > solBalance) {
      toast.error('Insufficient SOL balance');
      return { success: false, error: 'Insufficient balance' };
    }

    // Validate destination address
    try {
      new PublicKey(destinationAddress);
    } catch {
      toast.error('Invalid destination address');
      return { success: false, error: 'Invalid address' };
    }

    setIsWithdrawing(true);
    
    // Show pending toast
    const toastId = toast.loading('Network: Confirming...', {
      description: 'Broadcasting transaction to Solana'
    });

    try {
      // Reconstruct keypair from secret key
      const secretKeyBytes = bs58.decode(privateKey);
      const keypair = Keypair.fromSecretKey(secretKeyBytes);
      
      // Create transfer instruction
      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: keypair.publicKey,
          toPubkey: new PublicKey(destinationAddress),
          lamports: Math.floor(amountSol * LAMPORTS_PER_SOL),
        })
      );

      // Get recent blockhash
      const { blockhash, lastValidBlockHeight } = await connectionRef.current.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = keypair.publicKey;

      // Sign the transaction with local private key
      transaction.sign(keypair);

      // Broadcast to network
      const signature = await connectionRef.current.sendRawTransaction(
        transaction.serialize(),
        { skipPreflight: false, preflightCommitment: 'confirmed' }
      );

      // Wait for confirmation
      await connectionRef.current.confirmTransaction({
        signature,
        blockhash,
        lastValidBlockHeight
      });

      // Update toast to success
      toast.success('Withdrawal Complete!', {
        id: toastId,
        description: `Sent ${amountSol.toFixed(4)} SOL. Tx: ${signature.slice(0, 8)}...`
      });

      // Refresh balance
      await fetchSolBalance(false);

      setLastTransaction({
        type: 'withdraw',
        amount: amountSol,
        signature,
        timestamp: Date.now()
      });

      return { success: true, signature };
    } catch (error) {
      console.error('[GameWallet] Withdrawal error:', error);
      toast.error('Withdrawal Failed', {
        id: toastId,
        description: error.message
      });
      return { success: false, error: error.message };
    } finally {
      setIsWithdrawing(false);
    }
  }, [solanaAddress, localWallet, secretKey, solBalance, fetchSolBalance]);

  // Deposit credits to game wallet (converts $SLAP to in-game credits)
  // CRITICAL: After deposit, refresh from BLOCKCHAIN not server
  const deposit = useCallback(async (amount) => {
    if (!walletAddress && !user) {
      toast.error('Please connect wallet first');
      return { success: false, error: 'Not connected' };
    }

    setIsDepositing(true);
    const sessionId = walletAddress || 'default_session';

    try {
      const response = await axios.post(`${API}/wallet/deposit`, {
        session_id: sessionId,
        amount: amount
      });

      if (response.data.success) {
        // DO NOT use server balance - refresh from blockchain instead
        setLastTransaction({
          type: 'deposit',
          amount: amount,
          timestamp: Date.now()
        });
        
        toast.success(`Deposited ${amount} credits!`, {
          description: 'Refreshing balance from blockchain...'
        });
        
        // Refresh REAL balance from blockchain
        await fetchSlapTokenBalance(true);
        await refreshUser();
        
        return { success: true };
      } else {
        throw new Error(response.data.error || 'Deposit failed');
      }
    } catch (error) {
      console.error('Deposit error:', error);
      toast.error('Deposit failed', {
        description: error.response?.data?.detail || error.message
      });
      return { success: false, error: error.message };
    } finally {
      setIsDepositing(false);
    }
  }, [walletAddress, user, refreshUser, fetchSlapTokenBalance]);

  // Withdraw credits from game wallet (old method - for in-game credits)
  // CRITICAL: After withdraw, refresh from BLOCKCHAIN not server
  const withdraw = useCallback(async (amount) => {
    if (!walletAddress && !user) {
      toast.error('Please connect wallet first');
      return { success: false, error: 'Not connected' };
    }

    // Check against BLOCKCHAIN balance
    if (amount > slapBalance) {
      toast.error('Insufficient $SLAP balance');
      return { success: false, error: 'Insufficient balance' };
    }

    setIsWithdrawing(true);
    const sessionId = walletAddress || 'default_session';

    try {
      const response = await axios.post(`${API}/wallet/withdraw`, {
        session_id: sessionId,
        amount: amount
      });

      if (response.data.success) {
        // DO NOT use server balance - refresh from blockchain instead
        setLastTransaction({
          type: 'withdraw',
          amount: amount,
          timestamp: Date.now()
        });
        
        toast.success(`Withdrew ${amount} credits!`, {
          description: 'Refreshing balance from blockchain...'
        });
        
        // Refresh REAL balance from blockchain
        await fetchSlapTokenBalance(true);
        await refreshUser();
        
        return { success: true };
      } else {
        throw new Error(response.data.error || 'Withdrawal failed');
      }
    } catch (error) {
      console.error('Withdraw error:', error);
      toast.error('Withdrawal failed', {
        description: error.response?.data?.detail || error.message
      });
      return { success: false, error: error.message };
    } finally {
      setIsWithdrawing(false);
    }
  }, [walletAddress, user, credits, refreshUser]);

  // Spend credits (used by games internally)
  // FIXED: Uses LOCAL state, not broken backend API
  // For real on-chain transfers, use separate token transfer function
  const spendCredits = useCallback(async (amount, reason = 'gameplay') => {
    // Check against REAL blockchain balance (slapBalance), not DB credits
    const realBalance = slapBalance || credits;
    
    if (amount > realBalance) {
      console.log(`[GameWallet] Insufficient balance: need ${amount}, have ${realBalance}`);
      return { success: false, error: 'Insufficient balance' };
    }
    
    // For game entry fees: Just deduct from local state
    // The on-chain transfer to Treasury happens separately (or is handled by game win/loss)
    console.log(`[GameWallet] 💸 Spending ${amount} $SLAP for: ${reason}`);
    console.log(`[GameWallet] Balance: ${realBalance} → ${realBalance - amount}`);
    
    // Update local state immediately (optimistic)
    const newBalance = Math.max(0, realBalance - amount);
    setCredits(newBalance);
    setSlapBalance(newBalance);
    
    // Log the transaction
    setLastTransaction({
      type: 'spend',
      amount: amount,
      reason: reason,
      timestamp: Date.now()
    });
    
    return { success: true, newBalance: newBalance };
  }, [slapBalance, credits]);

  // Add credits (winnings from games)
  // FIXED: Uses LOCAL state for instant feedback
  const addCredits = useCallback(async (amount, reason = 'winnings') => {
    console.log(`[GameWallet] 🏆 Adding ${amount} $SLAP for: ${reason}`);
    
    // Update local state immediately
    const newBalance = (slapBalance || credits) + amount;
    setCredits(newBalance);
    setSlapBalance(newBalance);
    
    // Log the transaction
    setLastTransaction({
      type: 'add',
      amount: amount,
      reason: reason,
      timestamp: Date.now()
    });
    
    toast.success(`+${amount.toLocaleString()} $SLAP!`, {
      description: reason,
      icon: '🏆'
    });
    
    return { success: true, newBalance: newBalance };
  }, [slapBalance, credits]);

  // Dismiss onboarding banner
  const dismissOnboarding = useCallback(() => {
    setShowOnboarding(false);
    setHasSeenOnboarding(true);
    localStorage.setItem('gameWallet_onboarding_seen', 'true');
  }, []);

  // Refresh credits from server
  // CRITICAL FIX: NEVER override blockchain balance with server data
  // Server data is ONLY for game stats, not balance
  const refreshCredits = useCallback(async () => {
    console.log('[GameWallet] Refreshing balances from BLOCKCHAIN (ignoring server)...');
    
    // ONLY refresh from blockchain - NEVER trust server for balance
    await fetchSolBalance();
    await fetchSlapTokenBalance();
    
    // Server call is only for session validation, NOT balance
    // const sessionId = walletAddress || 'default_session';
    // We explicitly DO NOT update credits from server response
  }, [fetchSolBalance, fetchSlapTokenBalance]);

  // Manual refresh with toast feedback - fetches REAL blockchain data
  const manualRefresh = useCallback(async () => {
    toast.loading('Checking blockchain...', { id: 'refresh' });
    await fetchSolBalance(true);
    await fetchSlapTokenBalance(true);
    toast.dismiss('refresh');
  }, [fetchSolBalance, fetchSlapTokenBalance]);

  const value = {
    // Balances - ALL FROM BLOCKCHAIN
    credits, // $SLAP from blockchain
    slapBalance, // $SLAP from blockchain (same as credits)
    solBalance, // SOL from blockchain
    balanceError, // Error message if fetch failed
    
    // Status
    isWalletActive,
    isDepositing,
    isWithdrawing,
    isRefreshing,
    lastTransaction,
    connectionStatus,
    
    // DEBUG: RPC Status Indicators
    rpcEndpoint,
    rpcUrl,
    lastBalanceFetch,
    
    // Actions
    deposit,
    withdraw,
    withdrawSol,
    spendCredits,
    addCredits,
    refreshCredits,
    manualRefresh,
    fetchSolBalance,
    fetchSlapTokenBalance, // NEW: Fetch $SLAP from blockchain
    
    // Onboarding
    showOnboarding,
    dismissOnboarding,
    
    // Helpers
    hasEnoughCredits: (amount) => credits >= amount,
    formatCredits: (amount) => amount.toLocaleString(),
  };

  return (
    <GameWalletContext.Provider value={value}>
      {children}
    </GameWalletContext.Provider>
  );
};

export default GameWalletProvider;
